import { createRouter, createWebHistory } from 'vue-router'
import { useStore } from 'vuex'
import {
  artists,
  cards,
  configurations,
  dmas,
  editorials,
  livestreams,
  moments,
  playlists,
  podcasts,
  shows,
  stations,
  users,
  notifications,
  charts,
  layout,
  tips
} from "./modules"
import UserService from "../services/user-service"
import cache from "./modules/cache";
import { closeAsideMenu } from "../services/aside-menu";

const routes = [
  {
    path: '/',
    name: 'home',
    redirect: '/dashboard',
    component: () => import('../layouts/default-layout.vue'),
    meta: {
      middleware: 'auth',
    },
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('../views/dashboard/dashboard.vue'),
        meta: {
          pageTitle: 'Dashboard',
          breadcrumbs: ['Dashboards'],
        },
      },
      {
        path: '/artworks',
        name: 'artworks',
        component: () => import('../views/artworks/artworks.vue'),
        meta: {
          pageTitle: 'Artworks',
          breadcrumbs: ['Artworks'],
        },
      },
      {
        path: '/ads',
        name: 'ads',
        component: () => import('../views/ads/ads.vue'),
        meta: {
          pageTitle: 'Lottie Ad',
          breadcrumbs: ['Lottie Ad'],
        },
        children: [
          {
            path: '',
            name: 'ads-edit',
            component: () => import('../views/ads/edit.vue'),
            meta: {
              permissions: ['ads.update']
            }
          }
        ]
      },
      ...stations,
      ...livestreams,
      ...podcasts,
      ...artists,
      ...configurations,
      ...users,
      ...dmas,
      ...cards,
      ...editorials,
      ...moments,
      ...notifications,
      ...shows,
      ...playlists,
      ...charts,
      ...layout,
      ...tips,
      ...cache,
      {
        path: '/403',
        name: 'access-denied',
        meta: {
          pageTitle: '403 Access Denied',
          breadcrumbs: ['403'],
        },
        component: () => import('../views/errors/403.vue')
      },
      {
        path: '/:pathMatch(.*)*',
        name: 'page-not-found',
        meta: {
          pageTitle: '404 Not Found',
          breadcrumbs: ['404'],
        },
        component: () => import('../views/errors/404.vue')
      }
    ]
  },
  {
    path: '/',
    component: () => import('../layouts/auth-layout.vue'),
    children: [
      {
        path: '/sign-in',
        name: 'sign-in',
        component: () => import('../views/auth/sign-in.vue'),
        meta: {
          pageTitle: 'Sign In',
        },
      },
      {
        path: '/sign-up',
        name: 'sign-up',
        component: () => import('../views/auth/sign-up.vue'),
        meta: {
          pageTitle: 'Sign Up',
        },
      },
      {
        path: '/password/forgot',
        name: 'password-forgot',
        component: () => import('../views/auth/password-forgot.vue'),
        meta: {
          pageTitle: 'Forgot Password',
        },
      },
      {
        path: '/password/recover',
        name: 'password-recover',
        component: () => import('../views/auth/password-recover.vue'),
        meta: {
          pageTitle: 'Recover Password',
        },
      }
    ],
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior (to) {
    // If the route has a hash, scroll to the section with the specified ID; otherwise, scroll to the top of the page.
    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: 'smooth',
      };
    } else {
      return {
        top: 0,
        left: 0,
        behavior: 'smooth',
      };
    }
  },
})

router.beforeEach(async (to, from, next) => {
  const store = useStore()
  await store.dispatch('auth/checkToken')
  const isAuthenticated = store.getters['auth/isAuthenticated']
  let pageTitle = 'LaMusica CMS'

  if (to.meta?.pageTitle) {
    pageTitle = `${to.meta?.pageTitle} | ${pageTitle}`
  }

  document.title = pageTitle;

  store.dispatch('layout/setPageTitle', to.meta?.pageTitle || null)
  store.dispatch('layout/setBreadcrumbs', to.meta?.breadcrumbs || [])
  store.dispatch('layout/setActiveRoute', to.name || [])

  if (to.meta?.middleware === 'auth') {
    if (isAuthenticated) {
      if(to.meta?.permissions) {
        if(UserService.hasPermission(to.meta.permissions)) {
          next()
        } else {
          next({ name: 'access-denied' })
        }
      } else {
        next()
      }
    } else {
      next({ name: 'sign-in' });
    }
  } else {
    if (['sign-in'].includes(to.name) && isAuthenticated) {
      next({ name: 'dashboard' })
    } else {
      next()
    }
  }

})

router.afterEach(() => {
  closeAsideMenu()
})

export default router
